import { StyleSheet, css } from 'aphrodite'
import type { User } from '../types/user'
import CompanyInfoDisplay from './CompanyInfoDisplay'

interface ContactInfoDisplayProps {
  /** The user whose contact info to display */
  user: User | null | undefined
  /** Whether to show the company info below the contact details */
  showCompany?: boolean
  /** Display variant - 'card' shows in a styled card, 'inline' shows inline text */
  variant?: 'card' | 'inline'
  /** Whether to make email and phone clickable (mailto/tel links) */
  clickable?: boolean
  /** Custom class name for additional styling */
  className?: string
}

/**
 * Displays a user's contact information (name, email, phone) with optional
 * company info. Supports clickable email (mailto:) and phone (tel:) links.
 *
 * Used in:
 * - TemplateContactInfoStep (template creation form)
 * - ProjectConfirmation (confirmation page)
 * - ViewProjectModal (view project details modal)
 */
const ContactInfoDisplay: React.FC<ContactInfoDisplayProps> = ({
  user,
  showCompany = false,
  variant = 'card',
  clickable = true,
  className,
}) => {
  if (!user) return null

  const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim()

  // Inline variant - stacked line display
  if (variant === 'inline') {
    const hasContent = fullName || user.email || user.phone_number

    return (
      <>
        <div
          className={css(
            styles.inlineContainer,
            className && styles.customClass,
          )}
        >
          {hasContent ? (
            <>
              {fullName && (
                <div className={css(styles.inlineName)}>{fullName}</div>
              )}
              {/* Always render email row to maintain consistent layout */}
              <div className={css(styles.inlineDetail)}>
                {user.email ? (
                  clickable ? (
                    <a
                      href={`mailto:${user.email}`}
                      className={css(styles.inlineLink)}
                    >
                      {user.email}
                    </a>
                  ) : (
                    user.email
                  )
                ) : (
                  <span className={css(styles.emptyPlaceholder)}>&nbsp;</span>
                )}
              </div>
              {/* Always render phone row to maintain consistent layout */}
              <div className={css(styles.inlineDetail)}>
                {user.phone_number ? (
                  clickable ? (
                    <a
                      href={`tel:${user.phone_number}`}
                      className={css(styles.inlineLink)}
                    >
                      {user.phone_number}
                    </a>
                  ) : (
                    user.phone_number
                  )
                ) : (
                  <span className={css(styles.emptyPlaceholder)}>&nbsp;</span>
                )}
              </div>
            </>
          ) : (
            <span>-</span>
          )}
        </div>
        {showCompany && <CompanyInfoDisplay company={user.company} />}
      </>
    )
  }

  // Card variant - styled card display
  return (
    <>
      <div className={css(styles.container, className && styles.customClass)}>
        {fullName && <div className={css(styles.contactName)}>{fullName}</div>}
        {user.email && (
          <div className={css(styles.contactDetail)}>
            {clickable ? (
              <a
                href={`mailto:${user.email}`}
                className={css(styles.contactLink)}
              >
                {user.email}
              </a>
            ) : (
              user.email
            )}
          </div>
        )}
        {user.phone_number && (
          <div className={css(styles.contactDetail)}>
            {clickable ? (
              <a
                href={`tel:${user.phone_number}`}
                className={css(styles.contactLink)}
              >
                {user.phone_number}
              </a>
            ) : (
              user.phone_number
            )}
          </div>
        )}
      </div>
      {showCompany && <CompanyInfoDisplay company={user.company} />}
    </>
  )
}

const styles = StyleSheet.create({
  container: {
    marginTop: '8px',
    padding: '8px 12px',
    backgroundColor: '#F0F9FF',
    borderRadius: '6px',
    border: '1px solid #BAE6FD',
  },
  customClass: {},
  inlineContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  inlineName: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#111827',
    lineHeight: '1.4',
  },
  inlineDetail: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#6B7280',
    lineHeight: '1.4',
  },
  contactName: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '2px',
  },
  contactDetail: {
    fontSize: '12px',
    color: '#6B7280',
  },
  contactLink: {
    color: '#2563EB',
    textDecoration: 'none',
    ':hover': {
      textDecoration: 'underline',
    },
  },
  inlineLink: {
    color: '#2563EB',
    textDecoration: 'none',
    ':hover': {
      textDecoration: 'underline',
    },
  },
  emptyPlaceholder: {
    display: 'inline-block',
    minHeight: '1em',
  },
})

export default ContactInfoDisplay
